import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import type { AdresseFavorite, AdresseTrajet } from '../../types/ndf';
import { Champ, Saisie } from './ui/Champ';

/**
 * Choix d'une adresse parmi des propositions réelles.
 *
 * On ne prend JAMAIS la première réponse à la place de l'utilisateur : « gare
 * de Valence » a renvoyé le Tarn-et-Garonne au premier essai, soit 400 km
 * d'écart sur la distance, sans que rien ne le signale. C'est donc lui qui
 * choisit dans la liste, et l'adresse retenue s'affiche telle qu'elle sera
 * imprimée sur le justificatif.
 */
export function ChoixAdresse({
  label,
  adresse,
  favoris = [],
  onChange,
  onRetirer,
  onEnregistrerFavori,
}: {
  label: string;
  adresse: AdresseTrajet | null;
  /** Adresses déjà connues, proposées avant toute recherche. */
  favoris?: AdresseFavorite[];
  onChange: (a: AdresseTrajet | null) => void;
  onRetirer?: () => void;
  onEnregistrerFavori?: (a: AdresseTrajet) => void;
}) {
  const [recherche, setRecherche] = useState('');
  const [terme, setTerme] = useState('');

  // Une pause de frappe avant d'interroger : la Base Adresse Nationale est
  // gratuite, ce n'est pas une raison pour l'appeler à chaque lettre.
  useEffect(() => {
    const t = setTimeout(() => setTerme(recherche.trim()), 400);
    return () => clearTimeout(t);
  }, [recherche]);

  const propositions = useQuery({
    queryKey: ['ndf', 'adresses', terme],
    queryFn: () => ndfApi.trajets.adresses(terme),
    enabled: terme.length >= 3 && !adresse,
    retry: false,
  });

  if (adresse) {
    return (
      <Champ label={label}>
        <div className="flex min-h-11 items-center justify-between gap-2 rounded-md border border-nuit/20 bg-white px-3 py-2">
          <span className="text-sm text-nuit">{adresse.libelle}</span>
          <span className="flex shrink-0 gap-2">
            {/* Enregistrer une adresse ne se propose que si elle n'est pas
                déjà connue : un bouton qui ne fait rien use la confiance. */}
            {onEnregistrerFavori && !favoris.some((f) => f.libelle === adresse.libelle) && (
              <button
                type="button"
                onClick={() => onEnregistrerFavori(adresse)}
                title="Enregistrer cette adresse"
                className="text-sm text-nuit underline"
              >
                Enregistrer
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setRecherche('');
              }}
              className="text-sm text-nuit underline"
            >
              Changer
            </button>
            {onRetirer && (
              <button type="button" onClick={onRetirer} className="text-sm text-corail underline">
                Retirer
              </button>
            )}
          </span>
        </div>
      </Champ>
    );
  }

  return (
    <div>
      <Champ
        label={label}
        aide={
          propositions.isError
            ? 'La recherche d’adresses est indisponible. Réessayez dans un instant.'
            : undefined
        }
      >
        {/* Une adresse ne se corrige pas toute seule : la correction
            automatique transforme « Berjon » en « Bergson » et fait chercher
            une rue qui n'existe pas. */}
        <Saisie
          value={recherche}
          placeholder="Numéro, rue, ville…"
          autoCapitalize="words"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          onChange={(e) => setRecherche(e.target.value)}
        />
      </Champ>

      {/* Les favoris AVANT la recherche : on repart presque toujours des mêmes
          points, et taper une adresse qu'on a déjà donnée dix fois est du
          temps perdu. */}
      {favoris.length > 0 && recherche.length < 3 && (
        <ul className="mt-1 flex flex-wrap gap-1">
          {favoris.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                onClick={() => onChange(f)}
                title={f.libelle}
                className="min-h-11 rounded-full border border-nuit/20 px-3 text-sm text-nuit"
              >
                {f.libelle_court}
              </button>
            </li>
          ))}
        </ul>
      )}

      {propositions.isLoading && terme.length >= 3 && (
        <p className="mt-1 text-sm text-nuit/60">Recherche…</p>
      )}

      <ul className="divide-y divide-nuit/5">
        {(propositions.data?.adresses ?? []).map((a, i) => (
          <li key={`${a.libelle}-${i}`}>
            <button
              type="button"
              onClick={() => onChange(a)}
              className="min-h-11 w-full py-2 text-left text-sm text-nuit"
            >
              {a.libelle}
            </button>
          </li>
        ))}
      </ul>

      {terme.length >= 3 &&
        !propositions.isLoading &&
        !propositions.isError &&
        (propositions.data?.adresses ?? []).length === 0 && (
          <p className="mt-1 text-sm text-nuit/70">
            Aucune adresse trouvée. Essayez avec la ville, ou une rue voisine.
          </p>
        )}
    </div>
  );
}
